import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { UserPlus, Check, Loader2, RefreshCw, Zap, BadgeCheck, UserCog, Shield, X as XIcon } from "lucide-react";
import UserNameLink from "@/components/UserNameLink";
import { useAuth } from "@/lib/auth";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// Role assignment options used both at provisioning and inline-edit time.
// Mirrors the catalogue of /admin/users so the CEO can pick "Collaborateur Interne"
// vs "Collaborateur Freelance" directly from the Workspace directory.
const ROLE_OPTIONS = [
  { value: "COLLABORATOR_INTERNAL", label: "Collaborateur Interne", role: "COLLABORATOR", is_freelance: false },
  { value: "COLLABORATOR_FREELANCE", label: "Collaborateur Freelance", role: "COLLABORATOR", is_freelance: true },
  { value: "MANAGER", label: "Manager", role: "MANAGER", is_freelance: false },
  // Iter-228 — mirror AdminUsers.jsx: a MANAGER who is also flagged as freelance maps
  // to a dedicated combined option (was missing here → workspace directory showed
  // Manager Freelance users as plain "Manager" and the dropdown couldn't promote one).
  { value: "MANAGER_FREELANCE", label: "Manager Freelance", role: "MANAGER", is_freelance: true },
  { value: "GESTIONNAIRE", label: "Gestionnaire", role: "GESTIONNAIRE", is_freelance: false },
  { value: "ADMIN", label: "Admin", role: "ADMIN", is_freelance: false },
  { value: "CEO", label: "CEO", role: "CEO", is_freelance: false },
];

const userToRoleOption = (u) => {
  if (u.role === "COLLABORATOR") return u.is_freelance ? "COLLABORATOR_FREELANCE" : "COLLABORATOR_INTERNAL";
  // Iter-228 — a MANAGER + is_freelance is a "Manager Freelance".
  if (u.role === "MANAGER" && u.is_freelance) return "MANAGER_FREELANCE";
  return u.role;
};

export default function WorkspaceDirectory() {
  const { user } = useAuth();
  // Iter-209 — only the REAL CEO (non-sandbox) can prod-impersonate.
  const canImpersonate = user?.role === "CEO" && !user?.is_test;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("pending");
  const [dialog, setDialog] = useState(null);
  const [pickRole, setPickRole] = useState("COLLABORATOR_INTERNAL");
  // Iter-209 — impersonate dialog state.
  const [impTarget, setImpTarget] = useState(null);
  const [impReason, setImpReason] = useState("");
  const [impBusy, setImpBusy] = useState(false);
  // Iter-228x — Permissions matrix dialog state.
  const [permDialog, setPermDialog] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/workspace/directory");
      // Hydrate each row with the freelance flag from /users when has_profile so the
      // dropdown reflects the actual state (Internal vs Freelance) without an extra query.
      const dir = r.data;
      const linkedIds = dir.filter((u) => u.user_id).map((u) => u.user_id);
      let users = [];
      try {
        users = (await api.get("/users")).data;
      } catch (_) { /* permission-bound; fall back to dir-only view */ }
      const byId = Object.fromEntries(users.map((u) => [u.user_id, u]));
      setRows(dir.map((u) => (u.user_id && byId[u.user_id] ? { ...u, is_freelance: byId[u.user_id].is_freelance } : u)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load Workspace directory");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter-209 — CEO prod-impersonation flow (open dialog, post start, redirect).
  const openImpersonate = (u) => {
    setImpTarget(u);
    setImpReason("");
  };
  const confirmImpersonate = async () => {
    if (!impTarget?.user_id) return;
    setImpBusy(true);
    try {
      await api.post("/admin/impersonate/start", {
        target_user_id: impTarget.user_id,
        reason: impReason.trim() || null,
      });
      toast.success(`Impersonation démarrée · ${impTarget.name}`);
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer l'impersonation");
      setImpBusy(false);
    }
  };

  const provision = async () => {
    if (!dialog) return;
    const opt = ROLE_OPTIONS.find((o) => o.value === pickRole) || ROLE_OPTIONS[0];
    try {
      const res = await api.post("/workspace/provision", {
        email: dialog.primary_email,
        name: dialog.name,
        role: opt.role,
        is_freelance: opt.is_freelance,
      });
      toast.success(`${dialog.name} provisionné · ${opt.label}`);
      setDialog(null);
      // For Freelance: jump to AdminUsers with the auto-open dialog query so the CEO immediately
      // finalises the contract details (TJM, dates, cabinet, drives). The save in that dialog
      // is what ultimately validates the freelance attribution.
      if (opt.is_freelance && res.data?.user_id) {
        navigate(`/admin/users?openFreelance=${res.data.user_id}`);
        return;
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // For inline role-edit on linked rows: when the CEO switches an existing user TO Freelance,
  // we also redirect to the AdminUsers config dialog so they can fill the contract.
  const changeRoleForUser = async (user, optValue) => {
    const opt = ROLE_OPTIONS.find((o) => o.value === optValue);
    if (!opt) return;
    try {
      if (user.role !== opt.role) {
        await api.patch(`/users/${user.user_id}/role`, { role: opt.role });
      }
      // Iter-228 — generic handling of the is_freelance flag for BOTH
      // COLLABORATOR_FREELANCE and MANAGER_FREELANCE (the latter was previously
      // unsupported here, which silently wiped the freelance profile).
      if (opt.is_freelance && !user.is_freelance) {
        toast.info("Finalize the freelance configuration to validate the assignment.");
        navigate(`/admin/users?openFreelance=${user.user_id}`);
        return;
      }
      if (!opt.is_freelance && user.is_freelance) {
        if (window.confirm("Disable this user's freelance profile?")) {
          await api.delete(`/users/${user.user_id}/freelance`).catch(() => {});
        } else {
          await load();
          return;
        }
      }
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const autoSync = async () => {
    try {
      const r = await api.post("/workspace/auto-sync");
      toast.success(`Auto-provisioned ${r.data.created} user(s) as COLLABORATOR`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-sync failed"); }
  };

  const pending = rows.filter((r) => !r.has_profile);
  const linked = rows.filter((r) => r.has_profile);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-8 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Admin · Workspace</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Users & roles</h1>
          <p className="text-zinc-500 mt-3 max-w-xl">Synced from your Google Workspace directory. Provision profiles for users and assign roles — incluant le statut freelance.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={autoSync} data-testid="directory-autosync" disabled={loading} className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
            <Zap size={14} /> Auto-provision all pending
          </button>
          <button onClick={load} data-testid="directory-refresh" disabled={loading} className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-200 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800" data-testid="directory-error">
          <strong>Directory unavailable:</strong> {error}
          <div className="text-xs mt-1 text-red-700">The CEO account must be a Google Workspace admin for this to work. Check that admin.directory.user.readonly scope was granted at login.</div>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-zinc-200">
        {[
          { key: "pending", label: `Pending provisioning (${pending.length})` },
          { key: "linked", label: `Active profiles (${linked.length})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-4">Status / Role</div>
        </div>

        {(tab === "pending" ? pending : linked).map((u) => (
          <div key={u.primary_email} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`dir-row-${u.primary_email}`}>
            <div className="col-span-4 flex items-center gap-3 min-w-0">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-xs font-medium uppercase">{u.name?.slice(0, 2) || "U"}</div>
              )}
              <div className="font-medium truncate">
                {u.has_profile ? <UserNameLink user={u} testid={`dir-name-link-${u.user_id}`} /> : u.name}
              </div>
              {u.is_admin && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-950 text-white rounded font-mono">WS ADMIN</span>}
              {u.suspended && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono">SUSPENDED</span>}
            </div>
            <div className="col-span-4 text-sm text-zinc-700 truncate font-mono">{u.primary_email}</div>
            <div className="col-span-4 flex items-center justify-end gap-2">
              {u.has_profile ? (
                <>
                  {u.is_freelance && (
                    <span data-testid={`dir-freelance-badge-${u.user_id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-brand-orange-100 text-brand-orange-600 rounded font-mono uppercase">
                      <BadgeCheck size={10} /> Freelance
                    </span>
                  )}
                  <Select value={userToRoleOption(u)} onValueChange={(v) => changeRoleForUser(u, v)}>
                    <SelectTrigger className="w-56" data-testid={`dir-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`dir-role-opt-${o.value}-${u.user_id}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Iter-209 — Impersonate button (real CEO only).
                       Hidden for: self, other CEOs (defensive), inactive users. */}
                  {canImpersonate && u.user_id !== user.user_id && u.role !== "CEO" && (
                    <button
                      onClick={() => openImpersonate(u)}
                      data-testid={`impersonate-btn-${u.user_id}`}
                      title={`Impersonate ${u.name}`}
                      className="inline-flex items-center gap-1.5 px-2.5 h-9 bg-yellow-100 text-yellow-900 hover:bg-yellow-200 text-xs font-semibold rounded-md border border-yellow-300 whitespace-nowrap"
                    >
                      <UserCog size={13} /> Impersonate
                    </button>
                  )}
                  {/* Iter-228x — Permissions matrix for this user (CEO only) */}
                  {user?.role === "CEO" && u.role !== "CEO" && (
                    <button
                      onClick={() => setPermDialog({ user: u })}
                      data-testid={`view-perms-btn-${u.user_id}`}
                      title={`Voir les permissions de ${u.name}`}
                      className="inline-flex items-center gap-1.5 px-2.5 h-9 bg-zinc-100 text-zinc-700 hover:bg-zinc-200 text-xs font-semibold rounded-md border border-zinc-300 whitespace-nowrap"
                    >
                      <Shield size={13} /> Permissions
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => { setDialog(u); setPickRole("COLLABORATOR_INTERNAL"); }} data-testid={`provision-btn-${u.primary_email}`} className="inline-flex items-center gap-2 px-3 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
                  <UserPlus size={14} /> Provision
                </button>
              )}
            </div>
          </div>
        ))}

        {(tab === "pending" ? pending : linked).length === 0 && !loading && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">
            {tab === "pending" ? "All Workspace users are provisioned." : "No active profiles yet."}
          </div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Provision {dialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 font-mono text-xs">
              {dialog?.primary_email}
            </div>
            <div>
              <Label>Profile &amp; role</Label>
              <Select value={pickRole} onValueChange={setPickRole}>
                <SelectTrigger data-testid="provision-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`provision-role-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pickRole === "COLLABORATOR_FREELANCE" && (
                <p className="text-[11px] text-brand-orange-600 mt-2 inline-flex items-center gap-1">
                  <BadgeCheck size={12} /> Pensez à configurer TJM, dates et cabinet de portage dans Users &amp; Roles après création.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={provision} data-testid="provision-confirm" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md inline-flex items-center gap-2">
              <Check size={14} /> Create profile
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-209 — Prod-impersonate confirmation dialog. */}
      <Dialog open={!!impTarget} onOpenChange={(o) => { if (!o) { setImpTarget(null); setImpReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog size={18} className="text-yellow-700" /> Impersonate {impTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <div className="bg-yellow-50 border border-yellow-300 rounded-md px-4 py-3 text-yellow-900">
              <p className="font-semibold mb-1.5">Tu vas ouvrir une session au nom de cet utilisateur</p>
              <ul className="text-xs space-y-0.5 list-disc list-inside">
                <li>Tu verras l&apos;app <strong>exactement</strong> comme {impTarget?.name} la voit (permissions, données filtrées, etc.).</li>
                <li>Toutes tes actions seront tracées dans l&apos;<strong>audit log</strong> avec actor = toi (CEO).</li>
                <li>Tu peux revenir à ta session CEO à tout moment via le bandeau jaune en haut.</li>
                <li>La session expire automatiquement après <strong>4h</strong>.</li>
              </ul>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 font-mono text-xs">
              <div>{impTarget?.primary_email || impTarget?.email}</div>
              <div className="text-zinc-500 mt-1">Rôle cible : {impTarget?.role}</div>
            </div>
            <div>
              <Label htmlFor="imp-reason">Motif (optionnel mais recommandé)</Label>
              <Input
                id="imp-reason"
                data-testid="impersonate-reason"
                value={impReason}
                onChange={(e) => setImpReason(e.target.value)}
                placeholder="Ex: Debug bug ticket #1234, vérification permissions, etc."
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setImpTarget(null); setImpReason(""); }}
              disabled={impBusy}
              className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={confirmImpersonate}
              disabled={impBusy}
              data-testid="impersonate-confirm"
              className="px-4 h-10 bg-yellow-500 hover:bg-yellow-600 text-yellow-950 text-sm rounded-md inline-flex items-center gap-2 disabled:opacity-50"
            >
              {impBusy ? <Loader2 size={14} className="animate-spin" /> : <UserCog size={14} />}
              Démarrer l&apos;impersonation
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-228x — Permissions matrix dialog */}
      <UserPermissionsDialog
        target={permDialog?.user || null}
        onClose={() => setPermDialog(null)}
      />
    </div>
  );
}

// Iter-228x — Modal showing the full permissions catalog as checkboxes,
// pre-checked from the user's effective set. Toggling persists immediately
// to /admin/users/{id}/permissions and refreshes the local state. CEO targets
// are blocked at the backend (CEO_LOCKED) — defensive check here as well.
function UserPermissionsDialog({ target, onClose }) {
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/users/${target.user_id}/permissions`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Échec du chargement"))
      .finally(() => setLoading(false));
  }, [target]);

  const togglePerm = async (perm, nextChecked) => {
    if (!data) return;
    setBusyKey(perm);
    try {
      // Build the next override payload from current state.
      const granted = new Set(data.granted || []);
      const revoked = new Set(data.revoked || []);
      const inRoleDefault = (data.role_default || []).includes(perm)
        || (data.custom_role_perms || []).includes(perm);
      if (nextChecked) {
        // User wants the perm enabled
        if (inRoleDefault) {
          // Just remove from revoked
          revoked.delete(perm);
          granted.delete(perm);
        } else {
          // Add to granted
          granted.add(perm);
          revoked.delete(perm);
        }
      } else {
        // User wants the perm disabled
        if (inRoleDefault) {
          // Revoke it
          revoked.add(perm);
          granted.delete(perm);
        } else {
          // It was granted explicitly — just un-grant
          granted.delete(perm);
          revoked.delete(perm);
        }
      }
      const r = await api.put(`/admin/users/${target.user_id}/permissions`, {
        granted: Array.from(granted),
        revoked: Array.from(revoked),
      });
      setData(r.data);
      toast.success(`${nextChecked ? "Activée" : "Désactivée"} : ${perm}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusyKey(null);
    }
  };

  if (!target) return null;
  const effectiveSet = new Set(data?.effective || []);
  const roleDefaultSet = new Set(data?.role_default || []);
  const customRolePermsSet = new Set(data?.custom_role_perms || []);
  // Iter-228x-bis — A permission is "in the base profile" if it belongs to
  // the role default OR to the custom role. Permissions outside this set
  // cannot be granted to the user (the checkbox is disabled).
  const baseProfileSet = new Set([...roleDefaultSet, ...customRolePermsSet]);
  const grantedSet = new Set(data?.granted || []);
  const revokedSet = new Set(data?.revoked || []);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} /> Permissions — {target.name}
          </DialogTitle>
          <p className="text-[11px] text-zinc-500 leading-relaxed mt-1">
            Profil : <strong>{data?.role_label || target.role}</strong>.
            Décocher une permission par défaut la révoque pour cet utilisateur.
            Les permissions hors du profil de base sont désactivées
            (badge &laquo;&nbsp;hors profil&nbsp;&raquo;) — elles ne peuvent pas être accordées à un
            utilisateur qui ne les possède pas par défaut.
          </p>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-16 text-center text-sm text-zinc-500">
            <Loader2 size={18} className="mx-auto animate-spin mb-2" /> Chargement…
          </div>
        ) : data.ceo_locked ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            Le rôle CEO est verrouillé — permissions immuables.
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {Object.entries(data.catalog || {}).map(([module, entries]) => (
              <div key={module} className="border border-zinc-200 rounded-md overflow-hidden">
                <div className="bg-zinc-50 px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-mono text-zinc-700 font-semibold">
                  {module}
                </div>
                <div className="divide-y divide-zinc-100">
                  {(entries || []).map((p) => {
                    const checked = effectiveSet.has(p.key);
                    const isDefault = roleDefaultSet.has(p.key);
                    const isGranted = grantedSet.has(p.key);
                    const isRevoked = revokedSet.has(p.key);
                    // Iter-228x-bis — Permissions outside the user's base
                    // profile are read-only: the CEO cannot grant a brand
                    // new permission that the role does not normally cover.
                    const isOutsideProfile = !baseProfileSet.has(p.key);
                    const rowDisabled = isOutsideProfile || busyKey === p.key || data?.ceo_locked;
                    return (
                      <label
                        key={p.key}
                        data-testid={`perm-row-${p.key}`}
                        className={`flex items-start gap-3 px-3 py-2 ${isOutsideProfile ? "bg-zinc-50/60 cursor-not-allowed opacity-70" : "hover:bg-zinc-50 cursor-pointer"}`}
                        title={isOutsideProfile ? "Hors du profil de base — non assignable" : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={rowDisabled}
                          onChange={(e) => togglePerm(p.key, e.target.checked)}
                          data-testid={`perm-check-${p.key}`}
                          className="mt-1 h-4 w-4 accent-brand-navy disabled:cursor-not-allowed"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-medium ${isOutsideProfile ? "text-zinc-500" : "text-zinc-900"}`}>{p.label || p.key}</span>
                            <code className="text-[10px] font-mono text-zinc-400">{p.key}</code>
                            {isDefault && !isRevoked && (
                              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-zinc-100 text-zinc-600">défaut</span>
                            )}
                            {isGranted && (
                              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">ajoutée</span>
                            )}
                            {isRevoked && (
                              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-rose-100 text-rose-800">retirée</span>
                            )}
                            {isOutsideProfile && (
                              <span
                                className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-zinc-200 text-zinc-600"
                                data-testid={`perm-outside-profile-${p.key}`}
                              >
                                hors profil
                              </span>
                            )}
                            {busyKey === p.key && <Loader2 size={11} className="text-zinc-400 animate-spin" />}
                          </div>
                          {p.desc && <div className="text-[11px] text-zinc-500 mt-0.5">{p.desc}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <div className="text-[11px] text-zinc-500 mr-auto">
            {data && !data.ceo_locked && (
              <>
                <strong>{effectiveSet.size}</strong> permission(s) actives
                {(grantedSet.size + revokedSet.size > 0) && (
                  <> · <strong>{grantedSet.size}</strong> ajoutée(s), <strong>{revokedSet.size}</strong> retirée(s)</>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100"
            data-testid="user-permissions-close"
          >
            Fermer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

